/**
 * PromoCodesPage.tsx — Owner-only admin page to manage shop promo codes.
 *
 * Promo codes are applied as a whole-job discount on work orders & estimates.
 * Fields: code, discount_type (% or $), discount_value, max_uses (blank =
 * unlimited), starts_at / ends_at (optional window), is_active. The list shows
 * used_count. Mirrors the DiagnosisListsPage list-manager pattern.
 *
 * Route: /owner/settings/promo-codes  (owner-only; mounted under owner routes)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Input,
  InputNumber,
  Select,
  Switch,
  DatePicker,
  Modal,
  Form,
  Tag,
  Popconfirm,
  App,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  TagOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useIsOwner } from '../../components/auth/RoleGuard';
import {
  getPromoCodes,
  createPromoCode,
  updatePromoCode,
  deletePromoCode,
  PromoCode,
} from '../../services/promoCodesApi';

const { Title, Paragraph } = Typography;

interface PromoFormValues {
  code: string;
  discount_type: 'percentage' | 'amount';
  discount_value: number;
  max_uses?: number | null;
  range?: [Dayjs | null, Dayjs | null];
  is_active: boolean;
}

const PromoCodesPageInner: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const isOwner = useIsOwner();

  const rolePrefix = location.pathname.startsWith('/owner') ? '/owner' : '/admin';

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [promos, setPromos] = useState<PromoCode[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PromoCode | null>(null);
  const [form] = Form.useForm<PromoFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPromos(await getPromoCodes());
    } catch {
      message.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ discount_type: 'percentage', is_active: true });
    setModalOpen(true);
  };

  const openEdit = (record: PromoCode) => {
    setEditing(record);
    form.setFieldsValue({
      code: record.code,
      discount_type: record.discount_type,
      discount_value: record.discount_value,
      max_uses: record.max_uses ?? undefined,
      range:
        record.starts_at || record.ends_at
          ? [record.starts_at ? dayjs(record.starts_at) : null, record.ends_at ? dayjs(record.ends_at) : null]
          : undefined,
      is_active: record.is_active,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: PromoFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const [start, end] = values.range || [null, null];
    const payload = {
      code: values.code.trim(),
      discount_type: values.discount_type,
      discount_value: values.discount_value,
      max_uses: values.max_uses == null ? null : values.max_uses,
      starts_at: start ? start.toISOString() : null,
      ends_at: end ? end.toISOString() : null,
      is_active: values.is_active,
    };
    setBusy(true);
    try {
      if (editing) {
        await updatePromoCode(editing.id, payload);
        message.success('Promo code updated');
      } else {
        await createPromoCode(payload);
        message.success('Promo code created');
      }
      setModalOpen(false);
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to save promo code');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async (record: PromoCode, is_active: boolean) => {
    setBusy(true);
    try {
      await updatePromoCode(record.id, { is_active });
      await load();
    } catch {
      message.error('Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: number) => {
    setBusy(true);
    try {
      await deletePromoCode(id);
      message.success('Deleted');
      await load();
    } catch {
      message.error('Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  const fmtDiscount = (record: PromoCode) =>
    record.discount_type === 'percentage'
      ? `${record.discount_value}%`
      : `$${Number(record.discount_value).toFixed(2)}`;

  const fmtUses = (record: PromoCode) =>
    record.max_uses == null ? `${record.used_count} / ∞` : `${record.used_count} / ${record.max_uses}`;

  const fmtWindow = (record: PromoCode) => {
    if (!record.starts_at && !record.ends_at) return <span style={{ color: '#9ca3af' }}>Always</span>;
    const s = record.starts_at ? dayjs(record.starts_at).format('M/D/YY') : '…';
    const e = record.ends_at ? dayjs(record.ends_at).format('M/D/YY') : '…';
    return `${s} – ${e}`;
  };

  const columns: ColumnsType<PromoCode> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <Tag color="blue" style={{ fontFamily: 'monospace', fontSize: 13 }}>{code}</Tag>,
    },
    { title: 'Discount', key: 'discount', width: 110, render: (_v, r) => fmtDiscount(r) },
    { title: 'Used', key: 'used', width: 100, render: (_v, r) => fmtUses(r) },
    { title: 'Window', key: 'window', width: 150, render: (_v, r) => fmtWindow(r) },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (active: boolean, record) => (
        <Switch checked={active} disabled={busy} onChange={(c) => handleToggleActive(record, c)} />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_v, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} disabled={busy} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Delete this promo code?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} disabled={busy} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!isOwner) {
    return (
      <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`${rolePrefix}/settings`)}
          style={{ paddingLeft: 0 }}
        >
          Back to Settings
        </Button>
        <Paragraph type="secondary" style={{ marginTop: 16 }}>
          Promo code management is restricted to owners.
        </Paragraph>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`${rolePrefix}/settings`)}
          style={{ paddingLeft: 0 }}
        >
          Back to Settings
        </Button>
        <Title level={3} style={{ marginTop: 8 }}>
          <Space>
            <TagOutlined />
            Promo Codes
          </Space>
        </Title>
        <Paragraph type="secondary">
          Create discount codes customers or staff can apply to estimates and work orders.
        </Paragraph>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Card
          title="Active & Inactive Codes"
          styles={{ body: { paddingTop: 12 } }}
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={busy}>
              New Promo Code
            </Button>
          }
        >
          <Table
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={promos}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No promo codes yet' }}
          />
        </Card>
      )}

      <Modal
        title={editing ? 'Edit Promo Code' : 'New Promo Code'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={editing ? 'Save' : 'Create'}
        confirmLoading={busy}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="code"
            label="Code"
            rules={[{ required: true, message: 'Enter a code' }]}
          >
            <Input placeholder="e.g. SUMMER10" style={{ fontFamily: 'monospace' }} />
          </Form.Item>

          <Space style={{ display: 'flex' }} align="start">
            <Form.Item
              name="discount_type"
              label="Type"
              rules={[{ required: true }]}
              style={{ width: 160 }}
            >
              <Select
                options={[
                  { value: 'percentage', label: 'Percentage (%)' },
                  { value: 'amount', label: 'Amount ($)' },
                ]}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.discount_type !== cur.discount_type}
            >
              {({ getFieldValue }) => {
                const isPct = getFieldValue('discount_type') === 'percentage';
                return (
                  <Form.Item
                    name="discount_value"
                    label="Value"
                    rules={[{ required: true, message: 'Enter a value' }]}
                    style={{ width: 160 }}
                  >
                    <InputNumber
                      min={0}
                      max={isPct ? 100 : undefined}
                      precision={2}
                      style={{ width: '100%' }}
                      prefix={isPct ? '' : '$'}
                      suffix={isPct ? '%' : ''}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Space>

          <Form.Item
            name="max_uses"
            label="Max uses (blank = unlimited)"
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="Unlimited" />
          </Form.Item>

          <Form.Item name="range" label="Active window (optional)">
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              allowEmpty={[true, true]}
              showTime={false}
            />
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const PromoCodesPage: React.FC = () => (
  <App>
    <PromoCodesPageInner />
  </App>
);

export default PromoCodesPage;
